import { ItiHttpService } from "@/iti-core-react/services/iti-http-service";
import { ItiString } from "@/iti-core-react/utils/iti-string";
import { DsParameterDto } from "@/shared/dtos/Experimentation/ds-parameter-dto.ts";
import { ExperimentFilterDto } from "@/shared/dtos/Experimentation/experiment-filter-dto";
import { ExperimentSearchResultDto } from "@/shared/dtos/Experimentation/experiment-search-result-dto";
import Utils from "@/utils/utils";
import { API } from "../api";
import { ArtifactExperimentDto } from "../dtos/artifact-experiment-dto";
import { ExperimentDeploymentDto } from "../dtos/experiment-deployment";
import {
	ExperimentRepoRequestDto,
	ExperimentRepoResponseDto,
} from "../dtos/experiment-repo-dto";
import { ExperimentDto } from "../dtos/experimentation-dto";
import { ExternalProductDto } from "../dtos/external-products-dto";

export const ExperimentationService = {
	async searchExperiments(
		filter: ExperimentFilterDto,
	): Promise<ExperimentSearchResultDto> {
		const res = await ItiHttpService.post(
			API.experimentation.experimentsSearch,
			filter,
		);
		return {
			experiments: (res?.experiments ?? []).map(
				(item: ExperimentDto) => new ExperimentDto(item),
			),
			totalExperiments: res?.totalExperiments ?? 0,
		};
	},
	async getExperiment(id: string): Promise<ExperimentDto> {
		const res = await ItiHttpService.get(
			`${API.experimentation.experiments}/${id}`,
		);
		return new ExperimentDto(res);
	},
	async delete(id: string): Promise<boolean> {
		return await ItiHttpService.delete(`${API.gateway.experiments}/${id}`);
	},
	async create(experiment: ExperimentDto): Promise<ExperimentDto> {
		return await ItiHttpService.post(
			API.gateway.experiments,
			experiment.toJson(),
		);
	},
	async updateExperiment(experiment: ExperimentDto) {
		return await ItiHttpService.put(
			`${API.experimentation.experiments}/${experiment.id}`,
			experiment.toJson(),
		);
	},
	async getDatasetArtifactsByExperimentId(
		experimentId: string,
	): Promise<ArtifactExperimentDto[]> {
		const res = await ItiHttpService.get(
			`${API.experimentation.experiments}/${experimentId}/artifacts`,
		);
		return res.map(
			(item: ArtifactExperimentDto) => new ArtifactExperimentDto(item),
		);
	},
	async createArtifacts(
		experimentId: string,
		artifacts: ArtifactExperimentDto[],
	): Promise<ArtifactExperimentDto> {
		return await ItiHttpService.post(
			`${API.experimentation.experiments}/${experimentId}/artifacts`,
			artifacts,
		);
	},
	async deleteArtifact(
		experimentId: string,
		artifactId: string,
	): Promise<ArtifactExperimentDto> {
		return await ItiHttpService.delete(
			`${API.experimentation.experiments}/${experimentId}/artifacts/${artifactId}`,
		);
	},
	async checkRepo(
		params: ExperimentRepoRequestDto,
	): Promise<ExperimentRepoResponseDto> {
		return await ItiHttpService.post(
			API.experimentation.checkRepo,
			params,
			true,
			false,
			false,
		);
	},
	async getExternalProducts(): Promise<ExternalProductDto[]> {
		const res = await ItiHttpService.get(API.experimentation.externalProducts);
		return res.map((item: ExternalProductDto) => new ExternalProductDto(item));
	},
	async stopExperimentationDeployment(
		experimentId: string,
		deploymentId: string,
	): Promise<ExperimentDeploymentDto> {
		const res = await ItiHttpService.post(
			`${API.experimentation.experiments}/${experimentId}/stop/${deploymentId}`,
			{},
		);
		return res;
	},
	async stopExperimentationFromSession(): Promise<void> {
		const storedDeploymentId = sessionStorage.getItem("deploymentId");
		const experimentId = sessionStorage.getItem("experimentId");
		if (storedDeploymentId && experimentId) {
			await this.stopExperimentationDeployment(
				experimentId,
				storedDeploymentId,
			);
			Utils.clearSessionExperimentParams();
		}
	},
	async startExperimentation(
		id: string,
		body: { refreshToken: any; deploymentModeId: string },
	): Promise<ExperimentDeploymentDto> {
		const res = await ItiHttpService.post(
			`${API.experimentation.experiments}/${id}/start`,
			body,
		);
		return res;
	},
	async restoreExperimentation(id: string): Promise<string> {
		const res = await ItiHttpService.get(
			`${API.experimentation.experiments}/${id}/restore`,
			{},
		);
		return res;
	},

	async getExperimentDeploymentStatus(
		experimentId: string,
		deploymentId: string,
	): Promise<ExperimentDeploymentDto> {
		const res = await ItiHttpService.get(
			`${API.experimentation.experiments}/${experimentId}/status/${deploymentId}`,
			null,
			false,
		);
		return new ExperimentDeploymentDto(res);
	},
	async getDeploymentModes(id?: string): Promise<any> {
		return await ItiHttpService.get(
			ItiString.format(API.experimentation.deploymentModes, id),
		);
	},
	async getParameters(): Promise<DsParameterDto[]> {
		return await ItiHttpService.get(API.experimentation.parameters);
	},
};
